import { BudgetAmountForm } from '@/components/cashflow/budget/BudgetAmountForm';
import { BudgetOnboardingModalLoading } from '@/components/cashflow/budget/BudgetOnboardingModalLoading';
import { FlexRow } from '@/components/common/Flex';
import { useErrorToast } from '@/components/common/Toasts';
import { Header, Text } from '@/components/common/Typography';
import GoalIcon from '@/components/goals/GoalIcon';
import { logError } from '@/util/logger';
import { Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { BudgetFieldsFragment } from '@common/graphql/generated';
import {
    GoalWithMonthlyAmount,
    useGoalData,
    useSavingInvestingFormProps,
} from '@common/react/budget';
import { TOASTS } from '@common/toasts';
import { toDollars } from '@common/util/currency';
import { useModal } from '@ebay/nice-modal-react';
import { useBudgetOnboardingContext } from './BudgetOnboardingContext';
import { BudgetOnboardingLayout } from './BudgetOnboardingLayout';

export const BudgetOnboardingSavingInvesting = () => {
    const { budget, budgetLoading } = useBudgetOnboardingContext();
    const { goals, loading: goalsLoading } = useGoalData();

    if (budgetLoading || !budget || goalsLoading || !goals) {
        return <BudgetOnboardingModalLoading />;
    }

    return (
        <BudgetOnboardingSavingInvestingInner budget={budget} goals={goals} />
    );
};

const BudgetOnboardingSavingInvestingInner = ({
    budget,
    goals,
}: {
    budget: BudgetFieldsFragment;
    goals: GoalWithMonthlyAmount[];
}) => {
    const { goToNextStep, variant } = useBudgetOnboardingContext();
    const modal = useModal();
    const showErrorToast = useErrorToast();

    const { amount, setAmount, isSubmitting, handleSubmit } =
        useSavingInvestingFormProps({
            onComplete: () => {
                if (variant === 'create') {
                    goToNextStep();
                } else {
                    modal.remove();
                }
            },
            onError: (e) => {
                logError(e);
                showErrorToast(TOASTS.unexpectedError);
            },
            budget,
            goals,
        });

    return (
        <BudgetOnboardingLayout
            onContinue={handleSubmit}
            isContinueDisabled={false}
            isSubmitting={isSubmitting}
            buttonText={variant === 'create' ? 'Go to summary' : 'Save'}
            onClickEvent={
                variant === 'update'
                    ? 'Budget Onboarding Save Saving And Investing Button Pressed'
                    : 'Budget Onboarding Go To Summary Button Pressed'
            }
        >
            <Header>Saving & investing</Header>
            <BudgetAmountForm
                amount={amount}
                setAmount={setAmount}
                otherAmount={budget.lifestyleAmount + budget.fixedAmount}
                budgetPurpose="saving_investing"
            />
            {goals.length > 0 && (
                <Table mt={8}>
                    <Thead>
                        <Tr>
                            <Th>Name</Th>
                            <Th textAlign="right">Monthly contribution</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {goals.map((goal) => (
                            <GoalRow key={goal.id} goal={goal} />
                        ))}
                    </Tbody>
                </Table>
            )}
        </BudgetOnboardingLayout>
    );
};

const GoalRow = ({ goal }: { goal: GoalWithMonthlyAmount }) => (
    <Tr>
        <Td>
            <FlexRow align="center" gap={4}>
                <GoalIcon goal={goal} />
                <Text>{goal.name}</Text>
            </FlexRow>
        </Td>
        <Td textAlign="right">{toDollars(goal.monthlyAmount)}/mo</Td>
    </Tr>
);
